import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { brandPalette } from "../../../constants/brandPalette";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { RiSearch2Fill } from "react-icons/ri";
import ReactSelect from "react-select";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import {
  getLiveVideosRequest,
  liveVideoAbsenceRequest,
} from "../../../modules/admin-modules/liveVideos/Actions";

const focusRing = "0 0 0 1px rgba(91, 13, 22, 0.22)";
const PAGE_SIZE = 25;

const selectBase = adminSelectStyles({ menuZIndex: 1600 });

const selectStyles = {
  ...selectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...selectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
    minWidth: "260px",
  }),
};

const csvHeaders = [
  { label: "Student name", key: "name" },
  { label: "Code", key: "user_name" },
  { label: "Phone", key: "phone" },
  { label: "Parent phone", key: "parent_phone" },
];

function getLiveZoomId(live) {
  return live?.zoom_id ?? live?.id;
}

const LiveVideoAbsenceReport = () => {
  const dispatch = useDispatch();
  const [liveVideoId, setLiveVideoId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const liveVideos = useSelector((state) => state.adminLiveVideos.liveVideos);
  const absences = useSelector((state) => state.adminLiveVideos.liveVideoAbsences);
  const isLoading = useSelector((state) => state.adminLiveVideos.isLoading);

  useEffect(() => {
    dispatch(getLiveVideosRequest());
  }, [dispatch]);

  const liveOptions = useMemo(() => {
    const raw = Array.isArray(liveVideos) ? liveVideos : [];
    return raw.map((live) => ({
      value: String(live.id),
      label: live.title || `Live #${live.id}`,
    }));
  }, [liveVideos]);

  const selectedLive = useMemo(() => {
    const raw = Array.isArray(liveVideos) ? liveVideos : [];
    return raw.find((live) => String(live.id) === String(liveVideoId)) || null;
  }, [liveVideos, liveVideoId]);

  useEffect(() => {
    setSearchTerm("");
    setCurrentPage(1);
    if (!selectedLive) return;
    const zoomId = getLiveZoomId(selectedLive);
    if (zoomId == null) return;
    dispatch(liveVideoAbsenceRequest(zoomId));
  }, [selectedLive, dispatch]);

  const students = useMemo(() => {
    const raw = Array.isArray(absences) ? absences : [];
    const term = searchTerm.trim().toLowerCase();
    return raw
      .filter((item) => {
        if (!term) return true;
        const name = item?.name ?? item?.student?.name ?? "";
        const code = item?.user_name ?? item?.student?.user_name ?? "";
        const phone = item?.phone ?? item?.student?.phone ?? "";
        const parent = item?.parent_phone ?? item?.student?.parent_phone ?? "";
        return (
          name.toLowerCase().includes(term) ||
          code.toLowerCase().includes(term) ||
          phone.toLowerCase().includes(term) ||
          parent.toLowerCase().includes(term)
        );
      })
      .map((item, index) => ({
        key: item?.id ?? item?.student?.id ?? index,
        name: item?.name ?? item?.student?.name ?? "—",
        user_name: item?.user_name ?? item?.student?.user_name ?? "—",
        phone: item?.phone ?? item?.student?.phone ?? "—",
        parent_phone: item?.parent_phone ?? item?.student?.parent_phone ?? "—",
        index: index + 1,
      }));
  }, [absences, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(students.length / PAGE_SIZE));

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return students.slice(start, start + PAGE_SIZE);
  }, [students, currentPage]);

  const csvData = useMemo(
    () =>
      students.map((row) => ({
        name: row.name,
        user_name: row.user_name,
        phone: row.phone,
        parent_phone: row.parent_phone,
      })),
    [students]
  );

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
      >
        <Flex
          w="100%"
          mb={6}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text fontSize={{ base: "18px", md: "24px" }} fontWeight="900" color="var(--text-primary)">
              Live absence report
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              Students who did not join the selected live session.
            </Text>
          </Box>
          <Flex
            align="flex-start"
            gap={3}
            flexWrap="wrap"
            justifyContent={{ base: "flex-start", md: "flex-end" }}
          >
            <Box minW={{ base: "100%", md: "280px" }} maxW={{ base: "100%", md: "420px" }}>
              <ReactSelect
                options={liveOptions}
                placeholder="Select live session"
                styles={selectStyles}
                value={liveOptions.find((o) => o.value === String(liveVideoId)) || null}
                onChange={(opt) => setLiveVideoId(opt?.value ? String(opt.value) : "")}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                isClearable
                isLoading={isLoading && !liveVideoId}
              />
            </Box>
            <LogoutMenu />
          </Flex>
        </Flex>

        <Flex gap={4} mb={6} flexWrap="wrap" align="flex-end" justify="space-between">
          <Box flex="1" minW={{ base: "100%", md: "320px" }} maxW="420px">
            <Text
              fontSize="xs"
              fontWeight="800"
              color="var(--text-muted)"
              mb={2}
              textTransform="uppercase"
            >
              Search
            </Text>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <RiSearch2Fill color="var(--text-muted)" />
              </InputLeftElement>
              <Input
                placeholder="Name, code, or phone…"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                borderRadius="14px"
                borderColor="var(--border-subtle)"
                bg="var(--surface-muted)"
                color="var(--text-primary)"
                fontWeight="600"
                isDisabled={!liveVideoId}
                _focus={{ borderColor: brandPalette.primary, boxShadow: focusRing }}
              />
            </InputGroup>
          </Box>

          {liveVideoId && students.length > 0 ? (
            <CSVLink
              data={csvData}
              headers={csvHeaders}
              filename={`live-absence-${liveVideoId}-${dayjs().format("YYYY-MM-DD")}.csv`}
            >
              <Button
                size="sm"
                borderRadius="lg"
                bg={brandPalette.secondary}
                color="white"
                fontWeight="800"
                px={6}
                _hover={{ bg: brandPalette.primary }}
              >
                Export CSV
              </Button>
            </CSVLink>
          ) : null}
        </Flex>

        {liveVideoId && students.length > 0 ? (
          <Box
            mb={4}
            p={{ base: 4, md: 5 }}
            border="1px solid"
            borderColor="var(--border-subtle)"
            bg="var(--surface-muted)"
            borderRadius="16px"
          >
            <Text fontSize="sm" fontWeight="700" color="var(--text-primary)">
              {students.length} absent student{students.length === 1 ? "" : "s"}
            </Text>
          </Box>
        ) : null}

        <Box
          border="1px solid"
          borderColor="var(--border-subtle)"
          bg="var(--surface-raised)"
          borderRadius="18px"
          p={{ base: 4, md: 6 }}
          boxShadow="0 18px 55px rgba(0,0,0,0.12)"
          overflowX="auto"
          color="var(--text-primary)"
        >
          {!liveVideoId ? (
            <Text fontWeight="600" color="var(--text-muted)">
              Select a live session to load absences.
            </Text>
          ) : isLoading && students.length === 0 ? (
            <Text fontWeight="600" color="var(--text-primary)">
              Loading absences…
            </Text>
          ) : students.length === 0 ? (
            <Text fontWeight="600" color="var(--text-muted)">
              No absent students found for this live session.
            </Text>
          ) : (
            <>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th color="var(--text-primary)">#</Th>
                    <Th color="var(--text-primary)">Student</Th>
                    <Th color="var(--text-primary)">Code</Th>
                    <Th color="var(--text-primary)">Phone</Th>
                    <Th color="var(--text-primary)">Parent phone</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {pageRows.map((row) => (
                    <Tr key={row.key}>
                      <Td fontWeight="700">#{row.index}</Td>
                      <Td fontWeight="600">{row.name}</Td>
                      <Td>{row.user_name}</Td>
                      <Td>{row.phone}</Td>
                      <Td>{row.parent_phone}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>

              {totalPages > 1 ? (
                <Flex
                  mt={5}
                  justify="space-between"
                  align="center"
                  gap={3}
                  flexWrap="wrap"
                >
                  <Text fontSize="sm" fontWeight="600" color="var(--text-muted)">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1}–
                    {Math.min(currentPage * PAGE_SIZE, students.length)} of {students.length}
                  </Text>
                  <Flex gap={2}>
                    <Button
                      size="sm"
                      borderRadius="lg"
                      variant="outline"
                      borderColor="var(--border-subtle)"
                      isDisabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <Text fontSize="sm" fontWeight="700" alignSelf="center" px={2}>
                      {currentPage} / {totalPages}
                    </Text>
                    <Button
                      size="sm"
                      borderRadius="lg"
                      variant="outline"
                      borderColor="var(--border-subtle)"
                      isDisabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </Button>
                  </Flex>
                </Flex>
              ) : null}
            </>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default LiveVideoAbsenceReport;
